import { createSlice } from '@reduxjs/toolkit';

import { axios } from '@/api/axios';

import {
  LS_TOKEN_EXPIRES_AT_KEY,
  LS_TOKEN_KEY,
  LS_USER_BLOCKED_AT_PATH,
} from '@/constants/common';

import { User } from '@/types/user';

import { reloadPage, removeLocal } from '@/utils/browser';
import { clearOperationType } from '@/utils/operationType';

import {
  cancelSignOut,
  disableRefreshUser,
  enableRefreshUser,
  notifyUserChanged,
  notifyUserChangedSolved,
  requestSignOut,
  setLoggedInUser,
  signOut,
} from './actions';

interface AuthState {
  initialized: boolean;
  user?: User;
  shouldRefreshUser: boolean;
  isSignOutConfirming?: boolean;
  refreshUserDisabled: boolean;
}

export const INITIAL_AUTH_STATE: AuthState = {
  initialized: false,
  shouldRefreshUser: false,
  refreshUserDisabled: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_AUTH_STATE,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(setLoggedInUser, (state, { payload }) => {
      state.user = payload;
      if (!state.initialized) {
        state.initialized = true;
      }
      if (state.shouldRefreshUser) {
        state.shouldRefreshUser = false;
      }
    });
    builder.addCase(notifyUserChanged, (state, { payload }) => {
      state.shouldRefreshUser = true;
      if (payload?.resetInit) {
        state.initialized = false;
      }
    });
    builder.addCase(notifyUserChangedSolved, state => {
      state.shouldRefreshUser = false;
    });
    builder.addCase(signOut, state => {
      removeLocal(LS_TOKEN_KEY);
      removeLocal(LS_TOKEN_EXPIRES_AT_KEY);
      removeLocal(LS_USER_BLOCKED_AT_PATH);
      clearOperationType();
      sessionStorage.clear();
      axios.defaults.headers.common['Authorization'] = undefined;
      state.user = undefined;
      state.shouldRefreshUser = false;
      state.isSignOutConfirming = false;
      reloadPage();
    });
    builder.addCase(requestSignOut, state => {
      state.isSignOutConfirming = true;
    });
    builder.addCase(cancelSignOut, state => {
      state.isSignOutConfirming = false;
    });
    builder.addCase(disableRefreshUser, state => {
      state.refreshUserDisabled = true;
    });
    builder.addCase(enableRefreshUser, state => {
      state.refreshUserDisabled = false;
    });
  },
});

export const authReducer = authSlice.reducer;
